<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useDeviceState } from "@/stores/DeviceState";
import OfflineView from "./OfflineView.vue";
import SkeletonList from "@/components/UI/skeleton/SkeletonList.vue";
import type { DRDrawing, PaginationParams } from "@/types";
import {
  useGetArchivedDrawings,
  useUnarchiveDrawing,
  useDeleteDrawing,
  useUnarchiveManyDrawings,
  useDeleteManyDrawings,
} from "@/stores/queries";
import DebounceInput from "@/components/UI/form/DebounceInput.vue";
import DwgIcon from "@/components/UI/icons/DwgIcon.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRResponsiveBtn from "@/components/UI/DRResponsiveBtn.vue";
import DRSelector from "@/components/UI/DRSelector.vue";
import ProjectAvatar from "@/components/projects/ProjectAvatar.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import { useSelect } from "@/composable/useSelect";

import { useDates } from "@/composable/useDates";
import { useProjectsStore } from "@/stores/projectsStore";

const { formatDistanceToNow } = useDates();
const projectsStore = useProjectsStore();
const { getDrawingsWithProject } = projectsStore;
const { localProjects } = storeToRefs(projectsStore);
const device = useDeviceState();

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 25,
  sortBy: "archivedAt",
  order: "desc",
});

const query = ref<string>("");
const ALL_PROJECTS_VALUE = 0;
const selectedProjectId = ref<number>(ALL_PROJECTS_VALUE);

const { mutateAsync: unarchiveDrawing } = useUnarchiveDrawing();
const { mutateAsync: deleteDrawingMutation, isPending: isDeleting } =
  useDeleteDrawing();
const { mutateAsync: unarchiveManyMutation, isPending: isUnarchivingMany } =
  useUnarchiveManyDrawings();
const { mutateAsync: deleteManyMutation, isPending: isDeletingMany } =
  useDeleteManyDrawings();

const filters = computed<{ search?: string; projectId?: number }>(() => {
  const filters: { search?: string; projectId?: number } = {};
  if (query.value) {
    filters.search = query.value;
  }
  if (selectedProjectId.value) {
    filters.projectId = selectedProjectId.value;
  }
  return filters;
});

const {
  data: archivedDrawings,
  refetch,
  isLoading,
} = useGetArchivedDrawings(pagination, filters);

const drawings = computed(() =>
  getDrawingsWithProject(archivedDrawings.value ?? []).map((drawing) => ({
    ...drawing,
    archivedAtStr: drawing.archivedAt
      ? formatDistanceToNow(drawing.archivedAt)
      : undefined,
  }))
);

const drawingIds = computed(() => drawings.value.map((drawing) => drawing.id));
const { selectedIds, selectMode, setSelectedIds, toggleSelect, toggleAll } =
  useSelect(drawingIds);

function handleSelectAllChange(value: boolean | undefined | string | number) {
  toggleAll(value);
}

async function unArchive(drawing: DRDrawing) {
  unarchiveDrawing(drawing, {
    onSuccess: () => {
      refetch();
    },
  });
}

const drawingToDelete = ref<DRDrawing | undefined>(undefined);
const showConfirmDelete = computed({
  get: () => !!drawingToDelete.value,
  set: (value: boolean) => {
    if (!value) drawingToDelete.value = undefined;
  },
});

function confirmDelete(drawing: DRDrawing) {
  drawingToDelete.value = drawing;
}

async function deleteDrawing() {
  if (!drawingToDelete.value) return;
  await deleteDrawingMutation(drawingToDelete.value, {
    onSuccess: () => {
      drawingToDelete.value = undefined;
      refetch();
    },
  });
}

async function unarchiveSelection() {
  await unarchiveManyMutation(selectedIds.value, {
    onSuccess: () => {
      setSelectedIds([]);
      refetch();
    },
  });
}

const showConfirmDeleteMany = ref(false);
async function deleteSelection() {
  await deleteManyMutation(selectedIds.value, {
    onSuccess: () => {
      setSelectedIds([]);
      showConfirmDeleteMany.value = false;
      refetch();
    },
  });
}
</script>

<template>
  <div class="my-5 mx-3 mx-md-4 mx-lg-5 px-sm-2 px-md-4 px-lg-5">
    <OfflineView v-if="!device.isOnline" />

    <div v-else>
      <div
        class="d-flex flex-wrap justify-content-between align-items-center gap-2 my-2 pb-2"
      >
        <DRSelector
          :selectedIds="selectedIds"
          :itemsIds="drawingIds"
          v-model:selectMode="selectMode"
          @selectAll="handleSelectAllChange"
        >
          <template #actions>
            <DRResponsiveBtn
              variant="outline"
              :disabled="!device.isOnline"
              icon="bi-arrow-clockwise"
              :label="$t('dashboard:archives.unarchive')"
              :loading="isUnarchivingMany"
              @click="unarchiveSelection"
            />
            <DRResponsiveBtn
              variant="outline-danger"
              :disabled="!device.isOnline"
              icon="bi-trash"
              :label="$t('dashboard:archives.delete')"
              @click="showConfirmDeleteMany = true"
            />
          </template>
        </DRSelector>

        <div class="d-flex flex-wrap gap-2">
          <el-select
            v-model="selectedProjectId"
            :placeholder="$t('dashboard:archives.filterByProject')"
            style="width: 220px"
            size="large"
          >
            <el-option
              :value="ALL_PROJECTS_VALUE"
              :label="$t('dashboard:archives.allProjects')"
            />
            <el-option
              v-for="project in localProjects"
              :key="project.id"
              :label="project.name"
              :value="project.id"
            />
          </el-select>
          <DebounceInput
            v-model="query"
            :placeholder="$t('search')"
            :debounce="500"
            style="width: 200px"
            size="large"
            search
          />
        </div>
      </div>

      <div v-if="isLoading">
        <SkeletonList :count="5" height="3rem" />
      </div>
      <div v-else-if="drawings?.length">
        <ul id="files-list" class="list-group w-100">
          <li
            :key="drawing.id"
            v-for="drawing in drawings"
            class="list-group-item list-group-item-action d-inline-flex justify-content-between align-items-center bg-white border-0 py-3"
          >
            <div
              class="flex-grow-1 overflow-hidden text-truncate d-flex align-items-center gap-3"
            >
              <el-checkbox
                v-if="selectMode"
                :model-value="selectedIds.includes(drawing.id)"
                @change="toggleSelect(drawing.id)"
                @click.stop
              />
              <div class="col-auto"><DwgIcon :size="24" /></div>
              <div class="flew-grow-1 text-truncate">
                <div class="d-flex text-truncate">
                  <div class="col-12 text-truncate">
                    {{ drawing.name }}
                  </div>
                </div>
              </div>
              <div
                class="d-flex align-items-center gap-2 text-truncate"
                style="min-width: 10rem"
              >
                <ProjectAvatar
                  v-if="drawing.project"
                  :project="drawing.project"
                  :size="20"
                />
                <small class="text-muted text-truncate">
                  {{ drawing.project?.name }}
                </small>
              </div>
              <div style="min-width: 9rem">
                <small class="text-muted">
                  {{ drawing.archivedAtStr }}
                </small>
              </div>
            </div>
            <div class="col-auto d-flex gap-2 relative-position">
              <DRBtnIcon
                icon="bi-arrow-clockwise"
                variant="outline"
                :label="$t('dashboard:archives.unarchive')"
                :size="30"
                @click.prevent="unArchive(drawing)"
              >
              </DRBtnIcon>
              <DRBtnIcon
                icon="bi-trash"
                variant="outline-danger"
                :label="$t('dashboard:archives.delete')"
                :size="30"
                @click.prevent="confirmDelete(drawing)"
              >
              </DRBtnIcon>
            </div>
          </li>
        </ul>
      </div>
      <div v-else>
        <el-empty :description="$t('dashboard:archives.notFound')" />
      </div>
    </div>

    <ConfirmationModal
      v-model="showConfirmDelete"
      danger
      :title="$t('dashboard:drawings.delete.confirmation.title')"
      :message="$t('dashboard:drawings.delete.confirmation.message')"
      :loading="isDeleting"
      @confirm="deleteDrawing"
    />

    <ConfirmationModal
      v-model="showConfirmDeleteMany"
      danger
      :title="$t('dashboard:drawings.delete.confirmation.many.title')"
      :message="$t('dashboard:drawings.delete.confirmation.many.message')"
      :loading="isDeletingMany"
      @confirm="deleteSelection"
    />
  </div>
</template>

<style scoped>
.dr-input {
  max-width: 300px;
}
</style>
